import { SearchInput } from '@d-edge/overnight-hotelier-react'
import { useState, type ReactNode } from 'react'
import { IconButton } from '@/components/ui/Button'
import { Icons } from '@/components/ui/Icons'
import { Widget } from '@/components/ui/Widget'
import { useDebouncedValue } from '@/lib/hooks'
import styles from './MonitoringPage.module.css'

interface MonitoringCardProps {
  title: string
  open: boolean
  onToggle: () => void
  onRefresh: () => void
  fetching: boolean
  /** Receives the debounced search text; keep hooks out of it and render a component instead. */
  children: (query: string) => ReactNode
}

/** Collapsible card: the body, the refresh button and the name filter only exist while it is open. */
export function MonitoringCard({ title, open, onToggle, onRefresh, fetching, children }: MonitoringCardProps) {
  const [search, setSearch] = useState('')
  const query = useDebouncedValue(search, 250)

  return (
    <Widget
      title={title}
      onTitleClick={onToggle}
      loading={open && fetching}
      flush
      actions={
        <span className={styles.barActions}>
          {open && (
            <>
              <div className={styles.search}>
                <SearchInput icon fullWidth value={search} onChange={setSearch} placeholder="Filter by name" aria-label={`Filter ${title} by name`} clearButtonLabel="Clear filter" />
              </div>
              <IconButton label={`Refresh ${title}`} className={styles.barButton} onClick={onRefresh}>
                <Icons.Refresh size={15} />
              </IconButton>
            </>
          )}
          <IconButton label={`${open ? 'Close' : 'Open'} ${title}`} aria-expanded={open} className={styles.barButton} onClick={onToggle}>
            {open ? <Icons.ChevronDown size={15} /> : <Icons.ChevronRight size={15} />}
          </IconButton>
        </span>
      }
    >
      {open && <div className={styles.cardBody}>{children(query)}</div>}
    </Widget>
  )
}
